import { ServiceSlot } from '@/components/ChooseSlot';
import { address } from '@/components/Service/SubPages/ServiceRequest';
import { BOOK_SERVICE, generateBFFUrl, SERVICE_REQUEST_SUBMIT } from '@/configs/apiEndPoints';
import { serviceRequestType } from '@/context/serviceRequestContext';
import axiosInstance from './axiosConfig';
import { ACCOUNT_ID_QUERY_PARAM } from '../configs/serviceConstants';

export interface RequestSummaryType {
  serviceType: serviceRequestType;
  defectType: string;
  addnRemarks: string;
  serviceDate: Date;
  serviceSlot?: ServiceSlot;
  address: address;
  incidentId?: string;
}

interface SelectedProductType {
  accountId: string;
  hubCode: string;
  componentId: string;
}

export const getServiceType = (serviceType: serviceRequestType) => {
  switch (serviceType) {
    case 'reinstall':
    case 'REINSTALL':
      return 'RE_INSTALL';
    case 'repair':
      return 'REPAIR';
    case 'uninstall':
    case 'DISMANTLING':
      return 'UNINSTALL';
    default:
      return serviceType;
  }
};

const getReinstallationAddress = (serviceType: serviceRequestType, address: address) => {
  if (serviceType.toLowerCase() === 'reinstall') {
    return {
      address_line1: address.address1,
      pincode: address.postal_code,
      city: address.city,
      state: address.state,
    };
  }
  return null;
};

export const buildServiceRequestPayload = (
  selectedProduct: SelectedProductType,
  requestSummary: RequestSummaryType,
) => {
  const { accountId, componentId, hubCode } = selectedProduct;
  const { addnRemarks, serviceDate, serviceSlot, serviceType, defectType, address } =
    requestSummary;
  return {
    job_type: getServiceType(serviceType),
    additional_info: addnRemarks,
    case_origin: 'Website',
    asset_code: componentId,
    account_id: accountId,
    slot_date: serviceDate,
    selected_slot: serviceSlot,
    defect_type: defectType,
    reinstallation_address: getReinstallationAddress(serviceType, address),
    hub_code: hubCode,
  };
};

export const serviceRequestSummary = async (
  selectedProduct: SelectedProductType,
  requestSummary: RequestSummaryType,
) => {
  try {
    const serviceRequestSummaryData = await axiosInstance.post(
      generateBFFUrl(
        `${SERVICE_REQUEST_SUBMIT}?${ACCOUNT_ID_QUERY_PARAM}=${selectedProduct.accountId}`,
      ),
      buildServiceRequestPayload(selectedProduct, requestSummary),
    );
    return serviceRequestSummaryData;
  } catch (error) {
    throw error;
  }
};

export const bookService = async (
  selectedProduct: any,
  requestSummary: RequestSummaryType & {
    isFMS?: boolean;
    incidentType?: string;
    customerCode?: string;
    componentId?: string;
  },
) => {
  const { accountId, deviceId, id } = selectedProduct;
  const {
    addnRemarks,
    serviceDate,
    serviceSlot,
    serviceType,
    defectType,
    address,
    incidentId,
    isFMS,
  } = requestSummary;
  const requestBody = {
    accountId,
    productId: deviceId,
    assetId: id,
    jobType: serviceType,
    preferredSlot: serviceSlot?.slot?.startTime,
    workerCode: serviceSlot?.workerCodes,
    incidentId,
    description: defectType,
    reinstallationAddress: getReinstallationAddress(serviceType, address),
    sourceChannel: 'WEBSITE',
    ...(isFMS && {
      fms: true,
      incidentType: requestSummary.incidentType,
      customerCode: requestSummary.customerCode,
      componentId: requestSummary.componentId,
    }),
  };

  try {
    const bookServiceData = await axiosInstance.post(generateBFFUrl(BOOK_SERVICE), requestBody);
    return bookServiceData;
  } catch (error) {
    throw error;
  }
};
